import type { Ref } from 'vue'

import type {
  BonusCodeConditionPrice,
  BonusCodeConditionStatus,
  BonusCodeCoupon,
} from '~/utils/bonusCodeCondition'

import { useProductStore } from '#product/app/stores/product'
import { evaluateBonusCode } from '~/utils/bonusCodeCondition'

const BONUS_CODE_COUPON_STATE_KEY = 'bonusCode.coupon'

export const useStoredBonusCode = () => {
  const hypeStore = useHypeStore()

  const { couponCode: storedCouponCode } = storeToRefs(hypeStore)

  const couponSeen = computed({
    get: () => hypeStore.couponSeen,
    set: (value: boolean) => hypeStore.setCouponSeen(value),
  })

  const couponSubmitPending = computed({
    get: () => hypeStore.couponSubmitPending,
    set: (value: boolean) => hypeStore.setCouponSubmitPending(value),
  })

  const clearStoredCoupon = () => hypeStore.deleteCoupon()
  const storeCoupon = (code: string) => hypeStore.setCoupon(code)

  return { clearStoredCoupon, couponSeen, couponSubmitPending, storeCoupon, storedCouponCode }
}

export const useBonusCodeCondition = (
  currentPrice: Readonly<Ref<Maybe<BonusCodeConditionPrice> | undefined>> = ref(null),
) => {
  const route = useRoute()

  const productName = useProductGetter().getProductFromRoute(route)

  if (!productName)
    throw new Error(
      'useBonusCodeCondition can only be used in valid lottery, scratchcard or highfrequency routes',
    )

  const { clearStoredCoupon, couponSeen, storeCoupon, storedCouponCode } = useStoredBonusCode()

  const couponInfo = useState<Maybe<BonusCodeCoupon>>(BONUS_CODE_COUPON_STATE_KEY, () => null)

  const coupon = computed({
    get: () => couponInfo.value,
    set: (newCoupon) => {
      if (!newCoupon) {
        couponInfo.value = null
        clearStoredCoupon()
        return
      }

      const toastAlreadySeen = couponSeen.value

      couponInfo.value = newCoupon
      storeCoupon(newCoupon.info.code)

      if (toastAlreadySeen) couponSeen.value = true
    },
  })

  const productId = computed(() => useProductStore().getProduct(productName)?.id)

  const status = computed<BonusCodeConditionStatus>(() =>
    evaluateBonusCode(couponInfo.value, currentPrice.value),
  )

  return {
    clearStoredCoupon,
    coupon,
    couponSeen,
    productId,
    status,
    storedCouponCode,
  }
}
